import { get, set, del, clear } from 'idb-keyval';

/**
 * Cache Service for IndexedDB caching
 * Provides persistent caching for API responses, images, and other data
 * Data persists across browser sessions and page refreshes
 */
class CacheService {
  constructor() {
    this.CACHE_VERSION = '1.0';
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  }

  /**
   * Set data in IndexedDB cache with timestamp
   * @param {string} key - Cache key
   * @param {*} data - Data to cache (will be stringified)
   * @param {number} duration - Cache duration in milliseconds (optional)
   * @returns {Promise<boolean>} Success status
   */
  async setCache(key, data, duration = this.CACHE_DURATION) {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now(),
        version: this.CACHE_VERSION,
        expiresIn: duration
      };
      await set(key, JSON.stringify(cacheData));
      console.log(`✅ Cache set for key: ${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Failed to set cache for ${key}:`, error);
      return false;
    }
  }

  /**
   * Get data from IndexedDB cache
   * @param {string} key - Cache key
   * @param {boolean} checkExpiry - Whether to check if cache expired
   * @returns {Promise<*>} Cached data or null if not found/expired
   */
  async getCache(key, checkExpiry = true) {
    try {
      const cachedValue = await get(key);
      
      if (!cachedValue) {
        console.log(`⚠️ No cache found for key: ${key}`);
        return null;
      }

      const cacheData = JSON.parse(cachedValue);
      
      // Check if cache is expired
      if (checkExpiry) {
        const now = Date.now();
        const age = now - cacheData.timestamp;
        
        if (age > cacheData.expiresIn) {
          console.log(`⏰ Cache expired for key: ${key} (age: ${Math.round(age / 1000 / 60)} minutes)`);
          await this.deleteCache(key);
          return null;
        }
        
        console.log(`✅ Cache hit for key: ${key} (age: ${Math.round(age / 1000 / 60)} minutes)`);
      } else {
        console.log(`✅ Cache hit for key: ${key} (expiry check disabled)`);
      }

      return cacheData.data;
    } catch (error) {
      console.error(`❌ Failed to get cache for ${key}:`, error);
      return null;
    }
  }

  /**
   * Delete specific cache entry
   * @param {string} key - Cache key to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteCache(key) {
    try {
      await del(key);
      console.log(`🗑️ Cache deleted for key: ${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Failed to delete cache for ${key}:`, error);
      return false;
    }
  }

  /**
   * Clear all cache
   * @returns {Promise<boolean>} Success status
   */
  async clearAllCache() {
    try {
      await clear();
      console.log('🗑️ All cache cleared');
      return true;
    } catch (error) {
      console.error('❌ Failed to clear cache:', error);
      return false;
    }
  }

  /**
   * Check if cache exists and is valid
   * @param {string} key - Cache key
   * @returns {Promise<boolean>} True if valid cache exists
   */
  async hasValidCache(key) {
    const data = await this.getCache(key);
    return data !== null;
  }

  /**
   * Get cache age in minutes
   * @param {string} key - Cache key
   * @returns {Promise<number|null>} Age in minutes or null if cache doesn't exist
   */
  async getCacheAge(key) {
    try {
      const cachedValue = await get(key);
      if (!cachedValue) return null;

      const cacheData = JSON.parse(cachedValue);
      const age = Date.now() - cacheData.timestamp;
      return Math.round(age / 1000 / 60); // Return age in minutes
    } catch (error) {
      console.error(`❌ Failed to get cache age for ${key}:`, error);
      return null;
    }
  }
}

export default new CacheService();

